import { createContext, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
// firestore
import { collection, onSnapshot, doc, query, setDoc, deleteDoc, limit, where, getDocs } from "firebase/firestore";
import { db } from "../../services/firebase";
// products
import actionTypes from "../../common/actionTypes";
import { productReducer } from "../../reducers";
import {
  findProductsByTag,
  findProductsByVendor,
  findCollectionByTitle,
  findProductByTitleOrSku,
  findProductsByTagNextPage,
  findProductsByVendorNextPage,
  findProductByTitleOrSkuNextPage,
  findProducts,
  findProductsByCollectionId,
  findCollections,
} from "./schemas";
// context
import useAuthContext from "../../hooks/useAuthContext";
import useShopContext from "../../hooks/useShopContext";
import useActivityLogsContext from "../../hooks/useActivityLogsContext";
import { LOG_TYPES } from "../../contexts/ActivityLogsContext";
// constants
import { PRODUCT_DEFAULT_LIMIT } from "../../pages/Products/constants";
import {
  COLLECTION_TYPE,
  DATABASE_STATUTES,
  PRODUCT_TYPE,
  SETTINGS_TYPE,
  TAG_TYPE,
  VENDOR_TYPE,
  DEFAULT_PRODUCT_FILTER_VALUES,
  AXIOS_REQUEST_ERROR_CANCELLATION,
  FETCH_SIGNAL_ABORT_REASON,
  APP_ROUTES,
  // APP_ROUTES,
} from "../../common/constants"; // helpers
import {
  extractId,
  computeStatus,
  createProduct,
  processCSConfig,
  processISConfig,
  processBISConfig,
  getProductsFromIds,
  processPreorderConfig,
  getProductsByType,
  updateVariantConfigs,
} from "./preorderHelpers";
// hooks
import useCustomReducer from "../../hooks/useCustomReducer";
// utils
import shopifyAdminFetch from "../../utils/shopifyAdminFetch";

// product constants
const SHOPS = "Shops";
const PRODUCTS = "Products";
const storedRowsPerPage = 25;

const initialState = {
  timesactProducts: {
    keys: new Set(),
    entries: {},
    displayData: {
      po: [],
      bis: [],
      cs: [],
      is: [],
    },
  },
  products: {
    data: [],
    count: 0,
    pageInfo: null,
    cursor: null,
  },
  collections: {
    data: [],
    count: 0,
    cursor: null,
    pageInfo: null,
  },
  // Store products that have been filtered by name / sku / attributes (tags / vendor) etc.
  filtered: {
    data: [],
    entries: {},
    count: 0,
    hasNextPage: false,
  },
  statuses: {
    loadPreorder: DATABASE_STATUTES.NOT_LOADED,
    savePreorder: DATABASE_STATUTES.NOT_LOADED,
    loadProducts: DATABASE_STATUTES.NOT_LOADED,
    loadCollections: DATABASE_STATUTES.NOT_LOADED,
  },
  selectedItems: [],
  selectedItemsType: PRODUCT_TYPE,
  progress: 0,
  filters: {
    values: DEFAULT_PRODUCT_FILTER_VALUES,
    isActive: false,
  },
  loading: false,
  error: null,
};

const ProductsContext = createContext({
  ...initialState,
  fetchPreOrderedProducts: () => {},
  updatePreOrderProductVariant: () => Promise.resolve(),
  deletePreOrderProduct: () => Promise.resolve(),
  deletePreOrderProductVariants: () => Promise.resolve(),
  fetchProducts: () => Promise.resolve(),
  fetchCollections: () => Promise.resolve(),
  findProduct: () => Promise.resolve(),
  findProductByAttributes: () => Promise.resolve(),
  findCollection: () => Promise.resolve(),
  createTimesactProducts: () => Promise.resolve(),
  updateTimesactProducts: () => Promise.resolve(),
  saveSelectedProducts: () => {},
  deleteAllByType: () => Promise.resolve(),
  updateVariantConfig: () => Promise.resolve(),
  saveFilters: () => {},
  fetchAdditionalProductsByTitle: () => Promise.resolve(),
});

const ProductsProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(productReducer, initialState);

  const preOrderedProductsRef = useRef(null);
  const timesactProductsEntries = useRef({});
  const filteredEntries = useRef({});

  const { user } = useAuthContext();
  const { shop, addScriptTag, switchShowAskOrEmojiModal } = useShopContext();
  const { writeLog } = useActivityLogsContext();
  const { pathname } = useLocation();
  const isInitialSubscriptionActive = pathname.startsWith(APP_ROUTES.PRODUCTS.ROOT);

  const QUERY_INIT_ITERATION = storedRowsPerPage === 25 ? 5 : 3;

  const isFirstProductAdded = !shop.hasDoneOnboarding && !shop.hasReviewed && !state.timesactProducts.keys.size;

  timesactProductsEntries.current = state.timesactProducts.entries;
  filteredEntries.current = state.filtered.entries;

  const fetchAdditionalProductsByTitle = useCallback(
    async (title, type) => {
      try {
        dispatch({
          type: actionTypes.FETCH_ADDITIONAL_TIMESACT_PRODUCTS,
        });

        const data = [];
        const entries = {};

        const documentSnapshots = await getDocs(
          query(preOrderedProductsRef.current, where("title", ">=", title), where("title", "<=", `${title}~`))
        );

        documentSnapshots.docs.forEach((doc) => {
          data.push({
            ...doc.data(),
            id: doc.id,
          });
          entries[doc.id] = {
            ...doc.data(),
            id: doc.id,
          };
        });

        dispatch({
          type: actionTypes.FETCH_ADDITIONAL_TIMESACT_PRODUCTS_SUCCESS,
          payload: { data, type, entries },
        });
      } catch (error) {
        dispatch({
          type: actionTypes.FETCH_ADDITIONAL_TIMESACT_PRODUCTS_FAILURE,
          payload: error,
        });
      }
    },
    [dispatch]
  );

  const fetchPreOrderedProducts = useCallback(() => {
    if (user?.domain) {
      dispatch({
        type: actionTypes.FETCH_PRE_ORDERED_PRODUCTS,
      });

      preOrderedProductsRef.current = collection(db, SHOPS, user.domain, PRODUCTS);

      try {
        return onSnapshot(query(preOrderedProductsRef.current, limit(5000)), (snapshot) => {
          let entries = {};
          let keys = new Set();

          snapshot.forEach((doc) => {
            keys.add(doc.id);
            entries[doc.id] = {
              ...doc.data(),
              id: doc.id,
            };
          });

          dispatch({
            type: actionTypes.FETCH_PRE_ORDERED_PRODUCTS_SUCCESS,
            payload: {
              keys,
              entries,
            },
          });
        });
      } catch (error) {
        dispatch({
          type: actionTypes.FETCH_PRE_ORDERED_PRODUCTS_FAILURE,
          payload: error,
        });
      }
    }
  }, [dispatch, user?.domain]);

  const fetchProducts = useCallback(
    async ({ limit = PRODUCT_DEFAULT_LIMIT, cursor = null, count = 0 } = {}) => {
      dispatch({
        type: actionTypes.FETCH_PRODUCTS,
        isFetchingMoreProducts: cursor !== null,
      });

      try {
        let dataFromGQL = [];
        let query = cursor !== null ? findProducts({ limit, cursor }) : findProducts({ limit });

        const {
          data: { products = {} },
        } = await shopifyAdminFetch({ query });

        for (const edge of products.edges) {
          const product = {
            ...edge.node,
            id: parseInt(extractId(edge.node.id)),
            images: edge.node.images?.edges.map((image) => ({
              ...image.node,
            })),
            variants:
              edge.node.variants?.edges.map((variant) => ({
                ...variant.node,
                id: parseInt(extractId(variant.node.id)),
              })) || null,
            cursor: edge?.cursor,
            status: edge?.node.status,
            hasOnlyDefaultVariant: edge?.node.hasOnlyDefaultVariant,
            hasOutOfStockVariants: edge?.node.hasOutOfStockVariants,
            publishedAt: edge?.node.publishedAt,
          };

          dataFromGQL.push(product);
        }

        if (dataFromGQL.length) {
          dispatch({
            type: actionTypes.FETCH_PRODUCTS_SUCCESS,
            payload: {
              cursor: dataFromGQL.at(-1).cursor,
              count: count + dataFromGQL?.length,
              data: dataFromGQL,
              pageInfo: products.pageInfo,
            },
          });
        } else {
          dispatch({
            type: actionTypes.FETCH_PRODUCTS_FAILURE,
          });
        }
      } catch (error) {
        dispatch({
          type: actionTypes.FETCH_PRODUCTS_FAILURE,
          payload: error,
        });
      }
    },
    [dispatch]
  );

  const fetchCollections = useCallback(
    async ({ limit = PRODUCT_DEFAULT_LIMIT, count = 0, cursor = null } = {}) => {
      dispatch({
        type: actionTypes.FETCH_COLLECTIONS,
        isFetchingMoreCollections: cursor !== null,
      });

      try {
        let collectionsFromGQL = [];
        let query = findCollections({ limit, cursor });

        const {
          data: { collections = {} },
        } = await shopifyAdminFetch({ query });
        for (const edge of collections.edges) {
          const collection = {
            ...edge.node,
            id: parseInt(extractId(edge.node.id)),
            image: edge.node.image?.url,
            cursor: edge?.cursor,
          };

          collectionsFromGQL.push(collection);
        }

        dispatch({
          type: actionTypes.FETCH_COLLECTIONS_SUCCESS,
          payload: {
            cursor: collectionsFromGQL.at(-1).cursor,
            count: count + collectionsFromGQL?.length,
            data: collectionsFromGQL,
            pageInfo: collections.pageInfo,
          },
        });
      } catch (error) {
        dispatch({
          type: actionTypes.FETCH_COLLECTIONS_FAILURE,
          payload: error,
        });
      }
    },
    [dispatch]
  );

  const fetchNextChuckOfProducts = useCallback(
    async ({ cursor, query, prevResult, iteration }) => {
      try {
        if (query && !cursor && prevResult.pageInfo.hasNextPage && iteration < QUERY_INIT_ITERATION) {
          await sleep(2000);

          const { data } = await shopifyAdminFetch({
            query: findProductByTitleOrSkuNextPage({
              query,
              cursor: prevResult.edges.at(-1).cursor,
            }),
          });

          if (data?.products) {
            dispatch({
              type: actionTypes.FIND_PRODUCT_SUCCESS,
              payload: data.products,
            });

            fetchNextChuckOfProducts({
              cursor,
              query,
              prevResult: data.products,
              iteration: ++iteration,
            });
          }

          if (data?.errors?.length) {
            dispatch({
              type: actionTypes.FIND_PRODUCT_FAILURE,
              payload: data.errors[0],
            });
          }
        }
      } catch (error) {
        if (error.code !== AXIOS_REQUEST_ERROR_CANCELLATION && error !== FETCH_SIGNAL_ABORT_REASON) {
          dispatch({
            type: actionTypes.FIND_PRODUCT_FAILURE,
            payload: error,
          });
        }
      }
    },
    [QUERY_INIT_ITERATION, dispatch]
  );

  const fetchNextChuckOfProductsByAttr = useCallback(
    async ({ cursor, search, prevResult, attributeType, iteration }) => {
      try {
        if (search && !cursor && prevResult.pageInfo.hasNextPage && iteration < QUERY_INIT_ITERATION) {
          await sleep(2000);

          const queryCursor = prevResult.edges.at(-1).cursor;

          const query =
            attributeType === TAG_TYPE
              ? findProductsByTagNextPage({ query: search, cursor: queryCursor })
              : findProductsByVendorNextPage({
                  query: search,
                  cursor: queryCursor,
                });

          const { data } = await shopifyAdminFetch({ query });

          if (data?.products) {
            dispatch({
              type: actionTypes.FIND_PRODUCT_SUCCESS,
              payload: data.products,
            });

            fetchNextChuckOfProductsByAttr({
              cursor,
              search,
              prevResult: data.products,
              attributeType,
              iteration: ++iteration,
            });
          }

          if (data?.errors?.length) {
            dispatch({
              type: actionTypes.FIND_PRODUCT_FAILURE,
              payload: data.errors[0],
            });
          }
        }
      } catch (error) {
        if (error.code !== AXIOS_REQUEST_ERROR_CANCELLATION && error !== FETCH_SIGNAL_ABORT_REASON) {
          dispatch({
            type: actionTypes.FIND_PRODUCT_FAILURE,
            payload: error,
          });
        }
      }
    },
    [QUERY_INIT_ITERATION, dispatch]
  );

  const findProduct = useCallback(
    async ({ cursor = null, query } = {}) => {
      dispatch({
        type: actionTypes.FIND_PRODUCT,
        isFetchingMoreProducts: cursor !== null,
      });

      try {
        const { data } = await shopifyAdminFetch({
          query:
            cursor && query ? findProductByTitleOrSkuNextPage({ cursor, query }) : findProductByTitleOrSku({ query }),
        });

        if (data?.products) {
          dispatch({
            type: actionTypes.FIND_PRODUCT_SUCCESS,
            payload: data.products,
          });

          fetchNextChuckOfProducts({
            cursor,
            query,
            prevResult: data.products,
            iteration: 1,
          });
        }

        if (data?.errors?.length) {
          dispatch({
            type: actionTypes.FIND_PRODUCT_FAILURE,
            payload: data.errors[0],
          });
        }
      } catch (error) {
        if (error.code !== AXIOS_REQUEST_ERROR_CANCELLATION && error !== FETCH_SIGNAL_ABORT_REASON) {
          dispatch({
            type: actionTypes.FIND_PRODUCT_FAILURE,
            payload: error,
          });
        }
      }
    },
    [dispatch, fetchNextChuckOfProducts]
  );

  const findProductsByAttributes = useCallback(
    async ({ cursor = null, attributeType, query: search } = {}) => {
      dispatch({
        type: actionTypes.FIND_PRODUCT,
        isFetchingMoreProducts: cursor !== null,
      });

      search = search.trim();

      try {
        const query =
          attributeType === TAG_TYPE
            ? search && cursor
              ? findProductsByTagNextPage({ query: search, cursor })
              : findProductsByTag({ query: search })
            : search && cursor
              ? findProductsByVendorNextPage({ query: search, cursor })
              : findProductsByVendor({ query: search });

        const { data } = await shopifyAdminFetch({ query });

        if (data?.products) {
          dispatch({
            type: actionTypes.FIND_PRODUCT_SUCCESS,
            payload: data.products,
          });

          fetchNextChuckOfProductsByAttr({
            cursor,
            search,
            prevResult: data.products,
            attributeType,
            iteration: 1,
          });
        }

        if (data?.errors?.length) {
          dispatch({
            type: actionTypes.FIND_PRODUCT_FAILURE,
            payload: data.errors[0],
          });
        }
      } catch (error) {
        if (error.code !== AXIOS_REQUEST_ERROR_CANCELLATION && error !== FETCH_SIGNAL_ABORT_REASON) {
          dispatch({
            type: actionTypes.FIND_PRODUCT_FAILURE,
            payload: error,
          });
        }
      }
    },
    [dispatch, fetchNextChuckOfProductsByAttr]
  );

  const findCollection = useCallback(
    async ({ query } = {}) => {
      dispatch({
        type: actionTypes.FIND_COLLECTION,
      });

      try {
        const { data } = await shopifyAdminFetch({
          query: findCollectionByTitle({
            query,
          }),
        });

        if (data?.collections) {
          dispatch({
            type: actionTypes.FIND_COLLECTION_SUCCESS,
            payload: data.collections,
          });
        }

        if (data?.errors?.length) {
          dispatch({
            type: actionTypes.FIND_COLLECTION_FAILURE,
            payload: data.errors[0],
          });
        }
      } catch (error) {
        if (error.code !== AXIOS_REQUEST_ERROR_CANCELLATION && error !== FETCH_SIGNAL_ABORT_REASON) {
          dispatch({
            type: actionTypes.FIND_COLLECTION_FAILURE,
            payload: error,
          });
        }
      }
    },
    [dispatch]
  );

  const addProduct = useCallback(async ({ product, config, variantIds }) => {
    if (!timesactProductsEntries.current[product.id]) {
      // Product does not exist.
      await setDoc(
        doc(preOrderedProductsRef.current, String(product.id)),
        createProduct({ product, config, variantIds }),
        { merge: true }
      );
      return;
    }

    // Product exists; updating variants.
    const timesactProduct = timesactProductsEntries.current[product.id];

    if (product.variants && product.variants.length > 0) {
      const variants = timesactProduct.variants ? timesactProduct.variants : {};

      product.variants.forEach((variant) => {
        if (!variantIds.has(String(variant.id))) {
          return;
        }
        if (!variants[variant.id]) {
          // Variant does not exist.
          const productVariant = {
            quantity: variant.inventoryQuantity || 0,
            price: variant.price ? parseFloat(variant.price) : 0,
            title: variant.title || "",
            displayName: variant.displayName.endsWith("Default Title") ? product.title : variant.displayName || "",
            inventoryManagement: variant.inventoryManagement ? variant.inventoryManagement.toUpperCase() : "SHOPIFY",
            inventoryPolicy: variant.inventoryPolicy.toUpperCase() || "DENY",
            isPOEnabled: config.isPOEnabled,
            isBISEnabled: config.isBISEnabled,
            isCSEnabled: config.isCSEnabled,
            isISEnabled: config.isISEnabled,
            config: {
              ...config.config,
              status: config.isPOEnabled ? computeStatus(config.config, variant) : "NO_SET",
            },
            configBIS: {
              ...config.configBIS,
              status: config.isBISEnabled ? computeStatus(config.configBIS, variant) : "NO_SET",
            },
            configCS: {
              ...config.configCS,
              status: config.isCSEnabled ? computeStatus(config.configCS, variant) : "NO_SET",
            },
            configIS: {
              ...config.configIS,
              status: config.isISEnabled ? computeStatus(config.configIS, variant) : "NO_SET",
            },
            sku: variant.sku || "",
          };
          variants[variant.id] = productVariant;
        } else {
          // Update variant.
          variants[variant.id] = {
            ...variants[variant.id],
            title: variant.title || "",
            sku: variant.sku || "",
            displayName: variant.displayName.endsWith("Default Title") ? product.title : variant.displayName || "",
            isPOEnabled: config.isPOEnabled,
            isBISEnabled: config.isBISEnabled,
            isCSEnabled: config.isCSEnabled,
            isISEnabled: config.isISEnabled,
            config: {
              ...config.config,
              status: config.isPOEnabled ? computeStatus(config.config, variant) : "NO_SET",
            },
            configBIS: {
              ...config.configBIS,
              status: config.isBISEnabled ? computeStatus(config.configBIS, variant) : "NO_SET",
            },
            configCS: {
              ...config.configCS,
              status: config.isCSEnabled ? computeStatus(config.configCS, variant) : "NO_SET",
            },
            configIS: {
              ...config.configIS,
              status: config.isISEnabled ? computeStatus(config.configIS, variant) : "NO_SET",
            },
          };
        }
      });
      timesactProduct.variants = variants;
      timesactProduct.img =
        product.images && Array.isArray(product.images) && product.images.length > 0 ? product.images[0].src : "";
    }

    await setDoc(doc(preOrderedProductsRef.current, String(product.id)), timesactProduct, { merge: true });
  }, []);

  const updatePreOrderProductVariant = useCallback(
    async ({ productId, variantId, data }) => {
      const config = {
        ...data,
        config: processPreorderConfig(data.config),
        configBIS: processBISConfig(data.configBIS),
        configCS: processCSConfig(data.configCS),
        configIS: processISConfig(data.configIS),
      };

      const source = {
        ...timesactProductsEntries.current,
        ...filteredEntries.current,
      };
      const product = source[productId];
      const variant = product.variants[variantId];

      const variantProcessedData = {
        isPOEnabled: config.isPOEnabled,
        isBISEnabled: config.isBISEnabled,
        isCSEnabled: config.isCSEnabled,
        isISEnabled: config.isISEnabled,
        config: {
          ...config.config,
          status: config.isPOEnabled
            ? computeStatus(config.config, {
                inventoryQuantity: variant.quantity,
                inventoryManagement: variant.inventoryManagement,
              })
            : "NO_SET",
        },
        configBIS: {
          ...config.configBIS,
          status: config.isBISEnabled
            ? computeStatus(config.configBIS, {
                inventoryQuantity: variant.quantity,
                inventoryManagement: variant.inventoryManagement,
              })
            : "NO_SET",
        },
        configCS: {
          ...config.configCS,
          status: config.isCSEnabled
            ? computeStatus(config.configCS, {
                inventoryQuantity: variant.quantity,
                inventoryManagement: variant.inventoryManagement,
              })
            : "NO_SET",
        },
        configIS: {
          ...config.configIS,
          status: config.isISEnabled
            ? computeStatus(config.configIS, {
                inventoryQuantity: variant.quantity,
                inventoryManagement: variant.inventoryManagement,
              })
            : "NO_SET",
        },
      };

      await setDoc(
        doc(preOrderedProductsRef.current, productId),
        {
          variants: {
            [variantId]: variantProcessedData,
          },
        },
        { merge: true }
      );

      if (product && variant) {
        await writeLog(LOG_TYPES.UPDATE_VARIANT, {
          variantTitle: variant.title,
          productTitle: product.title,
        });
      }

      if (Object.keys(filteredEntries.current).length) {
        dispatch({
          type: actionTypes.UPDATE_FILTERED_PRODUCTS,
          payload: {
            productId,
            variantId,
            variantProcessedData,
          },
        });
      }

      return Promise.resolve();
    },
    [dispatch, writeLog]
  );

  const updateVariantConfig = useCallback(
    async ({ product, variant, config }) => {
      await setDoc(
        doc(preOrderedProductsRef.current, product.id),
        {
          variants: {
            [variant.id]: config,
          },
        },
        { merge: true }
      );

      if (product && variant) {
        await writeLog(LOG_TYPES.UPDATE_VARIANT, {
          variantTitle: variant.title,
          productTitle: product.title,
        });
      }

      if (Object.keys(filteredEntries.current).length) {
        dispatch({
          type: actionTypes.UPDATE_FILTERED_PRODUCTS,
          payload: {
            productId: product.id,
            variantId: variant.id,
            variantProcessedData: config,
          },
        });
      }

      return Promise.resolve();
    },
    [writeLog]
  );

  const updateVariants = useCallback(
    async (ids, type, config) => {
      const products = getProductsFromIds(ids);

      let updated = 0;
      let updatedProductIds = [];

      for (const [productId, variantIds] of Object.entries(products)) {
        const entry = timesactProductsEntries.current[productId];

        if (!entry || !entry.variants) {
          throw new Error(`Error[product_1]: Product id: ${productId}`);
        }

        for (const variantId of variantIds) {
          if (!entry.variants[variantId]) {
            throw new Error(`Error[product_1]: Product id & variant Id: ${productId} ${variantId}`);
          }

          if (type === SETTINGS_TYPE.PO) {
            entry.variants[variantId] = {
              ...entry.variants[variantId],
              isPOEnabled: config.isPOEnabled,
              config: {
                ...config.config,
                status: config.isPOEnabled
                  ? computeStatus(config.config, {
                      inventoryQuantity: entry.variants[variantId].quantity,
                      inventoryManagement: entry.variants[variantId].inventoryManagement,
                    })
                  : "NO_SET",
              },
            };
          }

          if (type === SETTINGS_TYPE.BIS) {
            entry.variants[variantId] = {
              ...entry.variants[variantId],
              isBISEnabled: config.isBISEnabled,
              configBIS: {
                ...config.configBIS,
                status: config.isBISEnabled
                  ? computeStatus(config.configBIS, {
                      inventoryQuantity: entry.variants[variantId].quantity,
                      inventoryManagement: entry.variants[variantId].inventoryManagement,
                    })
                  : "NO_SET",
              },
            };
          }

          if (type === SETTINGS_TYPE.CS) {
            entry.variants[variantId] = {
              ...entry.variants[variantId],
              isCSEnabled: config.isCSEnabled,
              configCS: {
                ...config.configCS,
                status: config.isCSEnabled
                  ? computeStatus(config.configCS, {
                      inventoryQuantity: entry.variants[variantId].quantity,
                      inventoryManagement: entry.variants[variantId].inventoryManagement,
                    })
                  : "NO_SET",
              },
            };
          }

          if (type === SETTINGS_TYPE.IS) {
            entry.variants[variantId] = {
              ...entry.variants[variantId],
              isISEnabled: config.isISEnabled,
              configIS: {
                ...config.configIS,
                status: config.isISEnabled
                  ? computeStatus(config.configIS, {
                      inventoryQuantity: entry.variants[variantId].quantity,
                      inventoryManagement: entry.variants[variantId].inventoryManagement,
                    })
                  : "NO_SET",
              },
            };
          }
        }

        await setDoc(doc(preOrderedProductsRef.current, productId), entry, {
          merge: true,
        });

        updated = updated + 1;

        dispatch({
          type: actionTypes.PROCESS_PRE_ORDERED_PRODUCTS_UPDATE,
          payload: (updated * 100) / Object.keys(products).length,
        });

        updatedProductIds.push(productId);

        await sleep(1500);
      }

      await writeLog(LOG_TYPES.UPDATE_PRODUCTS, {
        productCount: updated,
        variantCount: Object.values(products).reduce((acc, variants) => (acc += variants.size), 0),
      });
    },
    [dispatch, writeLog]
  );

  const addCollection = useCallback(
    async (id, productConfig, limit = PRODUCT_DEFAULT_LIMIT, pageInfo = null, cursor = null) => {
      // Extract products for that collection
      try {
        let getMoreProducts = true;

        while (getMoreProducts) {
          let dataFromGQL = [];
          let query =
            cursor !== null
              ? findProductsByCollectionId({ limit, collectionId: id, cursor })
              : findProductsByCollectionId({ limit, collectionId: id });

          const {
            data: { products = {} },
          } = await shopifyAdminFetch({ query });

          for (const edge of products.edges) {
            const product = {
              ...edge.node,
              id: parseInt(extractId(edge.node.id)),
              images: edge.node.images?.edges.map((image) => ({
                ...image.node,
              })),
              variants:
                edge.node.variants?.edges.map((variant) => ({
                  ...variant.node,
                  id: parseInt(extractId(variant.node.id)),
                })) || null,
              cursor: edge?.cursor,
              status: edge?.node.status,
              hasOnlyDefaultVariant: edge?.node.hasOnlyDefaultVariant,
              hasOutOfStockVariants: edge?.node.hasOutOfStockVariants,
              publishedAt: edge?.node.publishedAt,
            };

            dataFromGQL.push(product);
          }

          console.log("data addCollection", dataFromGQL);

          let added = 0;
          let addedVariants = 0;
          let createdProductIds = [];
          let updatedProductIds = [];

          for (let product of dataFromGQL) {
            await addProduct({
              product,
              config: productConfig,
              variantIds: new Set(Object.values(product.variants).map((variant) => String(variant.id))),
            });

            added += 1;
            addedVariants += product.variants.length;

            if (timesactProductsEntries.current[product.id]) {
              updatedProductIds.push(product.id);
            } else {
              createdProductIds.push(product.id);
            }

            dispatch({
              type: actionTypes.PROCESS_PRE_ORDERED_PRODUCTS_UPDATE,
              payload: (added * 100) / Object.keys(dataFromGQL).length,
            });

            await sleep(1000);
          }

          cursor = dataFromGQL.at(-1).cursor;
          pageInfo = products.pageInfo;

          if (!pageInfo.hasNextPage) {
            getMoreProducts = false;
          }

          await writeLog(LOG_TYPES.ADD_PRODUCTS, {
            productCount: added,
            variantCount: addedVariants,
          });
        }
      } catch (error) {
        dispatch({
          type: actionTypes.PROCESS_PRE_ORDERED_PRODUCTS_FAILURE,
          payload: error,
        });
      }
    },
    [addProduct, dispatch, writeLog]
  );

  const addByAttribute = useCallback(
    async (id, productConfig, attributeType, cursor = null) => {
      try {
        let getMoreProducts = true;

        while (getMoreProducts) {
          const isAttributeTag = attributeType === TAG_TYPE;
          const isNextChunk = id && cursor;
          let query = isAttributeTag
            ? isNextChunk
              ? findProductsByTagNextPage({ query: id, cursor })
              : findProductsByTag({ query: id })
            : isNextChunk
              ? findProductsByVendorNextPage({ query: id, cursor })
              : findProductsByVendor({ query: id });

          const {
            data: { products = {} },
          } = await shopifyAdminFetch({ query });
          // await axios.post(SHOPIFY_PROXY_URL, {
          //   query,
          // });

          let added = 0;
          let addedVariants = 0;
          let createdProductIds = [];
          let updatedProductIds = [];

          for (const edge of products.edges) {
            const product = {
              ...edge.node,
              id: parseInt(extractId(edge.node.id)),
              images: edge.node.images?.edges.map((image) => ({
                ...image.node,
              })),
              variants:
                edge.node.variants?.edges.map((variant) => ({
                  ...variant.node,
                  id: parseInt(extractId(variant.node.id)),
                })) || null,
            };

            await addProduct({
              product,
              config: productConfig,
              variantIds: new Set(Object.values(product.variants).map((variant) => String(variant.id))),
            });

            added += 1;
            addedVariants += product.variants.length;

            if (timesactProductsEntries.current[product.id]) {
              updatedProductIds.push(product.id);
            } else {
              createdProductIds.push(product.id);
            }

            dispatch({
              type: actionTypes.PROCESS_PRE_ORDERED_PRODUCTS_UPDATE,
              payload: (added * 100) / Object.keys(products.edges).length,
            });

            await sleep(1000);

            cursor = edge.cursor;
          }

          if (!products.pageInfo.hasNextPage) {
            getMoreProducts = false;
          }

          await writeLog(LOG_TYPES.ADD_PRODUCTS, {
            productCount: added,
            variantCount: addedVariants,
          });
        }
      } catch (error) {
        dispatch({
          type: actionTypes.PROCESS_PRE_ORDERED_PRODUCTS_FAILURE,
          payload: error,
        });
      }
    },
    [addProduct, dispatch, writeLog]
  );

  const createTimesactProducts = useCallback(
    async ({ type, data, ids }) => {
      try {
        dispatch({
          type: actionTypes.PROCESS_PRE_ORDERED_PRODUCTS,
        });

        const processedConfig = {
          ...data,
          config: processPreorderConfig(data.config),
          configBIS: processBISConfig(data.configBIS),
          configCS: processCSConfig(data.configCS),
          configIS: processISConfig(data.configIS),
        };

        // Adding by products.
        if (type === PRODUCT_TYPE) {
          const products = getProductsFromIds(ids);

          let added = 0;
          let createdProductIds = [];
          let updatedProductIds = [];

          for (const productId of Object.keys(products)) {
            // "product" - selected product
            let product = state.products.data.find(({ id }) => String(id) === productId);

            // get product from filtered products.
            if (!product) {
              product = state.filtered.data.find(({ id }) => String(id) === productId);
            }

            if (product) {
              await addProduct({
                product,
                config: processedConfig,
                variantIds: products[productId],
              });

              added = added + 1;

              if (state.timesactProducts.entries[product.id]) {
                updatedProductIds.push(product.id);
              } else {
                createdProductIds.push(product.id);
              }

              dispatch({
                type: actionTypes.PROCESS_PRE_ORDERED_PRODUCTS_UPDATE,
                payload: (added * 100) / Object.keys(products).length,
              });

              await sleep(1000);
            }
          }

          await writeLog(LOG_TYPES.ADD_PRODUCTS, {
            productCount: added,
            variantCount: Object.values(products).reduce((acc, variants) => (acc += variants.size), 0),
          });
        }

        // Adding by collections.
        if (type === COLLECTION_TYPE) {
          // Adding new products.
          for (const id of ids) {
            await addCollection(id, processedConfig);
          }
        }

        // Adding by attributes.
        if (type === TAG_TYPE || type === VENDOR_TYPE) {
          for (const id of ids) {
            await addByAttribute(id, processedConfig, type);
          }
        }

        // Script Tags are not used anymore. They are replaced with theme app extension.
        // if (!shop.hasScript && (!shop.hasCustomScript || (shop.hasCustomScript && shop.needsScriptUpdate))) {
        //   if (shop.hasSellingPlanEnabled) {
        //     await addScriptTag("timesactV3.js");
        //   } else {
        //     await addScriptTag("timesact.js");
        //   }
        // }

        if (shop.hasScript && !shop.hasCustomScript && shop.scriptVersion === "0.0") {
          if (shop.hasSellingPlanEnabled) {
            await addScriptTag("timesactV3.js");
          } else {
            await addScriptTag("timesact.js");
          }
        }

        if (isFirstProductAdded) {
          switchShowAskOrEmojiModal(true);
        }

        dispatch({
          type: actionTypes.PROCESS_PRE_ORDERED_PRODUCTS_SUCCESS,
        });

        return Promise.resolve();
      } catch (error) {
        dispatch({
          type: actionTypes.PROCESS_PRE_ORDERED_PRODUCTS_FAILURE,
          payload: error,
        });
      }
    },
    [
      addByAttribute,
      addCollection,
      addProduct,
      addScriptTag,
      dispatch,
      isFirstProductAdded,
      shop.hasCustomScript,
      shop.hasScript,
      shop.hasSellingPlanEnabled,
      shop.needsScriptUpdate,
      shop.scriptVersion,
      state.filtered.data,
      state.products.data,
      writeLog,
      state.timesactProducts?.entries,
    ]
  );

  const updateTimesactProducts = useCallback(
    async ({ tab, data, ids }) => {
      dispatch({
        type: actionTypes.PROCESS_PRE_ORDERED_PRODUCTS,
      });

      const processedConfig = {
        ...data,
        config: processPreorderConfig(data.config),
        configBIS: processBISConfig(data.configBIS),
        configCS: processCSConfig(data.configCS),
        configIS: processISConfig(data.configIS),
      };

      await updateVariants(ids, tab, processedConfig);

      dispatch({
        type: actionTypes.PROCESS_PRE_ORDERED_PRODUCTS_SUCCESS,
      });
    },
    [dispatch, updateVariants]
  );

  useEffect(() => {
    if (isInitialSubscriptionActive && user?.domain) {
      fetchPreOrderedProducts();
    }
  }, [isInitialSubscriptionActive, user?.domain, fetchPreOrderedProducts]);

  const deletePreOrderProduct = useCallback(
    async ({ id }) => {
      await deleteDoc(doc(db, SHOPS, user.domain, PRODUCTS, id));
    },
    [user?.domain]
  );

  const deletePreOrderProductVariants = useCallback(
    async (selectedIds, type) => {
      dispatch({
        type: actionTypes.PROCESS_PRE_ORDERED_PRODUCTS,
      });

      let deleted = 0;
      let deletedVariants = 0;
      let deletedProductIds = [];
      let updatedProductIds = [];
      const productVariantIds = getProductsFromIds(selectedIds);
      const deletedProductLength = Object.keys(productVariantIds).length;
      const storedProducts = {
        ...timesactProductsEntries.current,
        ...filteredEntries.current,
      };

      for (const [productId, variantIds] of Object.entries(productVariantIds)) {
        const entry = storedProducts[productId];

        if (!entry || !entry.variants) {
          throw new Error(`Error[product_1]: Product id: ${productId}`);
        }

        for (const variantId of variantIds) {
          if (!entry.variants[variantId]) {
            throw new Error(`Error[product_1]: Product id & variant Id: ${productId} ${variantId}`);
          }
          updateVariantConfigs(entry.variants[variantId], type);

          deletedVariants += 1;
        }

        if (
          Object.keys(entry.variants).every((variantId) => {
            if ("isPOEnabled" in entry.variants[variantId]) {
              return (
                !entry.variants[variantId].isPOEnabled &&
                !entry.variants[variantId].isBISEnabled &&
                !entry.variants[variantId].isCSEnabled &&
                !entry.variants[variantId].isISEnabled
              );
            }
            // [BACKWARDS COMPATIBILITY] There is no BIS or CS config for this product.
            return entry.variants[variantId].config.status === "NO_SET";
          })
        ) {
          await deletePreOrderProduct({ id: productId });

          await writeLog(LOG_TYPES.DELETE_PRODUCT, entry.title);

          deletedProductIds.push(productId);
        } else {
          await setDoc(doc(db, SHOPS, user.domain, PRODUCTS, productId), entry, {
            merge: true,
          });

          await writeLog(LOG_TYPES.DELETE_PRODUCT_VARIANTS, { count: deletedVariants, productTitle: entry.title });

          updatedProductIds.push(productId);
        }

        deleted = deleted + 1;
        dispatch({
          type: actionTypes.PROCESS_PRE_ORDERED_PRODUCTS_UPDATE,
          payload: (deleted * 100) / deletedProductLength,
        });

        await sleep(2000);
      }

      if (shop.hasMultipleProducts) {
        dispatch({
          type: actionTypes.REMOVE_VARIANTS_IN_FILTERED_PRODUCTS,
          payload: productVariantIds,
        });
      }

      dispatch({
        type: actionTypes.PROCESS_PRE_ORDERED_PRODUCTS_SUCCESS,
      });

      return Promise.resolve();
    },
    [deletePreOrderProduct, user?.domain, shop.hasMultipleProducts]
  );

  const deleteAllByType = useCallback(
    async (type) => {
      dispatch({
        type: actionTypes.PROCESS_PRE_ORDERED_PRODUCTS,
      });

      let deleted = 0;
      let deletedProductIds = [];
      let updatedProductIds = [];
      const storedProducts = getProductsByType(type, {
        ...timesactProductsEntries.current,
        ...filteredEntries.current,
      });

      const deletedProductLength = storedProducts.length;

      for (const product of storedProducts) {
        if (!product.variants) {
          throw new Error(`Error[product_1]: Product id: ${product.id}`);
        }

        const copiedProduct = structuredClone(product);

        for (const variant of Object.values(copiedProduct.variants)) {
          updateVariantConfigs(variant, type);
        }

        if (
          Object.values(copiedProduct.variants).every((variant) => {
            if ("isPOEnabled" in variant) {
              return !variant.isPOEnabled && !variant.isBISEnabled && !variant.isCSEnabled && !variant.isISEnabled;
            }

            return variant.config.status === "NO_SET";
          })
        ) {
          await deletePreOrderProduct({ id: product.id });

          deletedProductIds.push(product.id);
        } else {
          await setDoc(doc(db, SHOPS, user.domain, PRODUCTS, product.id), copiedProduct, {
            merge: true,
          });

          updatedProductIds.push(product.id);
        }

        deleted = deleted + 1;

        dispatch({
          type: actionTypes.PROCESS_PRE_ORDERED_PRODUCTS_UPDATE,
          payload: (deleted * 100) / deletedProductLength,
        });

        if (type === SETTINGS_TYPE.PO || type === SETTINGS_TYPE.IS) {
          await sleep(1000);
        }
      }

      await writeLog(LOG_TYPES.DELETE_ALL_PRODUCTS, type);

      dispatch({
        type: actionTypes.REMOVE_ALL_FILTERED_PRODUCTS,
      });

      dispatch({
        type: actionTypes.PROCESS_PRE_ORDERED_PRODUCTS_SUCCESS,
      });
    },
    [deletePreOrderProduct, dispatch, user?.domain]
  );

  const sleep = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

  // Save selected product ids to store
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const saveSelectedProducts = ({ ids, type }) => {
    dispatch({
      type: actionTypes.SAVE_PRODUCT_IDS,
      payload: {
        ids,
        type,
      },
    });
  };

  const saveFilters = useCallback(
    ({ values, isActive }) => {
      dispatch({
        type: actionTypes.SAVE_PRODUCT_FILTERS,
        payload: {
          values,
          isActive,
        },
      });
    },
    [dispatch]
  );

  const getCurrentProductById = (productId) => {
    const source = {
      ...timesactProductsEntries.current,
      ...filteredEntries.current,
    };

    return source[productId];
  };

  const memoizedState = useMemo(
    () => ({
      ...state,
      dispatch,
      // fetchPreOrderedProducts,
      updatePreOrderProductVariant,
      deletePreOrderProductVariants,
      fetchProducts,
      fetchCollections,
      findProduct,
      findProductsByAttributes,
      findCollection,
      createTimesactProducts,
      updateTimesactProducts,
      saveSelectedProducts,
      deleteAllByType,
      updateVariantConfig,
      saveFilters,
      fetchAdditionalProductsByTitle,
      getCurrentProductById,
    }),
    [
      state,
      dispatch,
      createTimesactProducts,
      deleteAllByType,
      deletePreOrderProductVariants,
      fetchCollections,
      // fetchPreOrderedProducts,
      fetchProducts,
      findCollection,
      findProduct,
      findProductsByAttributes,
      updatePreOrderProductVariant,
      updateVariantConfig,
      updateTimesactProducts,
      saveFilters,
      // UNNECESSARY DEPENDENCIES
      // fetchAdditionalProductsByTitle,
      // saveSelectedProducts,
    ]
  );

  return <ProductsContext.Provider value={memoizedState}>{children}</ProductsContext.Provider>;
};

export { ProductsContext, ProductsProvider };
